import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';

interface WhatsAppPreviewProps {
  header?: string;
  body: string;
  imageUrl?: string;
}

const BUBBLE_BG = '#D9FDD3'; // WhatsApp light-green outgoing bubble
const CHAT_BG = '#E5DDD5'; // WhatsApp chat backdrop
const PLACEHOLDER_RE = /(\{\{\s*\d+\s*\}\})/g;

function formatTime(date = new Date()): string {
  const h = date.getHours();
  const m = date.getMinutes();
  const hh = ((h + 11) % 12) + 1;
  const mm = m.toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${hh}:${mm} ${ampm}`;
}

/**
 * Splits the input into runs, marking {{n}} placeholders so they can be highlighted.
 * Also turns *bold* segments into bold spans (WhatsApp formatting).
 */
function renderText(text: string): React.ReactNode {
  if (!text) return null;

  // First split on placeholders
  const parts = text.split(PLACEHOLDER_RE);
  return parts.map((part, idx) => {
    if (PLACEHOLDER_RE.test(part)) {
      // Reset regex (since global)
      PLACEHOLDER_RE.lastIndex = 0;
      return (
        <Box
          key={`ph-${idx}`}
          component="span"
          sx={{
            bgcolor: 'rgba(0, 150, 136, 0.15)',
            color: '#00796B',
            px: 0.5,
            borderRadius: 0.5,
            fontWeight: 600,
            fontFamily: 'monospace',
          }}
        >
          {part}
        </Box>
      );
    }
    return renderBold(part, idx);
  });
}

function renderBold(text: string, keyPrefix: number): React.ReactNode {
  const segments = text.split(/(\*[^*\n]+\*)/g);
  return segments.map((seg, i) => {
    if (/^\*[^*\n]+\*$/.test(seg)) {
      return (
        <strong key={`b-${keyPrefix}-${i}`}>{seg.slice(1, -1)}</strong>
      );
    }
    return <React.Fragment key={`t-${keyPrefix}-${i}`}>{seg}</React.Fragment>;
  });
}

export default function WhatsAppPreview({ header, body, imageUrl }: WhatsAppPreviewProps) {
  const time = useMemo(() => formatTime(), []);
  const trimmedHeader = (header || '').trim();
  const trimmedBody = (body || '').trim();
  const trimmedImageUrl = (imageUrl || '').trim();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 360,
        mx: 'auto',
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        bgcolor: '#fff',
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          bgcolor: '#075E54',
          color: '#fff',
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          H
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
            Huttons
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            online
          </Typography>
        </Box>
      </Box>

      {/* Chat body */}
      <Box
        sx={{
          bgcolor: CHAT_BG,
          px: 1.5,
          py: 2,
          minHeight: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%2740%27 height=%2740%27><circle cx=%272%27 cy=%272%27 r=%270.6%27 fill=%27%23000%27 fill-opacity=%270.04%27/></svg>")',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '88%',
            bgcolor: BUBBLE_BG,
            px: 1.25,
            py: 0.75,
            borderRadius: 1.5,
            borderTopRightRadius: 0,
            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: -7,
              width: 0,
              height: 0,
              borderTop: `8px solid ${BUBBLE_BG}`,
              borderRight: '7px solid transparent',
            },
          }}
        >
          {trimmedImageUrl && (
            <Box
              sx={{
                mx: -1.25,
                mt: -0.75,
                mb: 0.75,
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.05)',
                aspectRatio: '4 / 3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                key={trimmedImageUrl}
                component="img"
                src={trimmedImageUrl}
                alt="Header"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onLoad={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = '';
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          )}
          {trimmedHeader && !trimmedImageUrl && (
            <Typography
              sx={{
                fontWeight: 700,
                color: '#111B21',
                fontSize: 14.5,
                lineHeight: 1.3,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {renderText(trimmedHeader)}
            </Typography>
          )}
          <Typography
            sx={{
              color: trimmedBody ? '#111B21' : 'rgba(0,0,0,0.4)',
              fontSize: 14.5,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mt: trimmedHeader || trimmedImageUrl ? 0.25 : 0,
              fontStyle: trimmedBody ? 'normal' : 'italic',
            }}
          >
            {trimmedBody ? renderText(trimmedBody) : 'Your message will appear here…'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              mt: 0.25,
            }}
          >
            <Typography sx={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>{time}</Typography>
            <DoneAllIcon sx={{ fontSize: 14, color: '#53BDEB' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
